// Ubicación en el repo: src/lib/ProfessionalContext.jsx
// Ficha del profesional actualmente logueado (su fila en `professionals` +
// las sucursales que tiene asignadas en `professional_branches`). Se usa
// para armar el selector de sucursal activa y para decisiones de UX (ej.
// mostrar el botón de eliminar reforzado solo si `isAdmin`) — nunca como
// mecanismo de seguridad real, eso lo sigue resolviendo RLS en el backend
// (ver spec-pacientes-agenda.md 1.5).
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';

const ProfessionalContext = createContext(undefined);

// status: 'idle' (sin sesión aún) | 'loading' | 'ready' | 'error'
const initialState = { status: 'idle', professional: null, branchIds: [] };

export function ProfessionalProvider({ children }) {
  const { status: sessionStatus, session, mfaSatisfied } = useSession();
  const [state, setState] = useState(initialState);

  const authedReady = sessionStatus === 'ready' && !!session && mfaSatisfied;

  useEffect(() => {
    let cancelled = false;

    if (!authedReady) {
      setState(initialState);
      return;
    }

    setState((s) => ({ ...s, status: 'loading' }));

    async function load() {
      const { data: professional, error: profError } = await supabase
        .from('professionals')
        .select('id, nombre, apellido, role, is_admin, mfa_required')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (profError || !professional) {
        if (!cancelled) setState({ status: 'error', professional: null, branchIds: [] });
        return;
      }

      const { data: branches } = await supabase
        .from('professional_branches')
        .select('branch_id')
        .eq('professional_id', professional.id);

      if (!cancelled) {
        setState({
          status: 'ready',
          professional,
          branchIds: (branches ?? []).map((b) => b.branch_id),
        });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedReady, session?.user?.id]);

  return <ProfessionalContext.Provider value={state}>{children}</ProfessionalContext.Provider>;
}

export function useCurrentProfessional() {
  const ctx = useContext(ProfessionalContext);
  if (ctx === undefined) {
    throw new Error('useCurrentProfessional debe usarse dentro de <ProfessionalProvider>');
  }
  return ctx;
}
